/**
 * Per-card payment projection.
 *
 * THE INTERLEAVING RULE — stated once, evaluated in one place:
 *
 *   For each card, for each statement month, exactly one of
 *   {plan rows, implicit minimum} applies. A month is COVERED iff an ACTIVE
 *   non-lump-sum plan's [startDate, endDate] contains that month's due date.
 *   Months outside that window fall back to the computed minimum.
 *   LUMP_SUM plans never cover a month — their payments STACK on top.
 *
 * Every code path in the old engine got some version of this wrong, in a
 * different way each time. "Does this card have a plan" was treated as a
 * boolean when it is inherently date-scoped.
 *
 * Stale plans: when the user edits a card balance and chooses "leave plan
 * as-is", the stored rows' balances no longer match reality. We therefore use
 * the stored AMOUNTS (that is what the user will actually pay) but recompute
 * interest and running balance LIVE. The projection stays internally
 * consistent instead of quietly reporting a balance that cannot be true.
 */

import { CreditCard, PayoffPlan, PlannedPayment, PaymentStatus } from '../../types';
import { payDayFor, startOfDay, toLocalDay } from './dates';
import { computeMinimumPayment, cycleInterest } from './minimums';
import { firstPaymentDate } from './planMath';

const EPSILON = 0.005;
const MAX_CYCLES = 720; // 60 years — a hard stop, never reached in practice

export type CardPaymentKind = 'SCHEDULED' | 'EXTRA' | 'FINAL' | 'MINIMUM';

export interface CardPaymentEvent {
  cardId: string;
  cardName: string;
  planId: string | null;
  paymentId: string | null;
  date: Date;
  amount: number;
  paymentKind: CardPaymentKind;
  status: PaymentStatus;
  openingBalance: number;
  interestAccrued: number;
  /** Card balance immediately after this payment. Replaces the old
   *  string-parsing `getRemainingBalanceAfterPayment` in Calendar. */
  remainingAfter: number;
  /** True when this payment closes the card. Replaces the old
   *  `isFinalCreditCardPayment`, which reverse-engineered the card by
   *  stripping words out of the transaction description. */
  isFinal: boolean;
}

function activePlansFor(cardId: string, plans: PayoffPlan[]): PayoffPlan[] {
  return plans.filter(p => p.creditCardId === cardId && p.status === 'ACTIVE');
}


/**
 * Project every payment for one card from its `balanceAsOf` anchor through
 * `through`, in date order.
 */
export function projectCardPayments(input: {
  card: CreditCard;
  plans: PayoffPlan[];
  through: Date;
}): CardPaymentEvent[] {
  const { card, through } = input;
  const plans = activePlansFor(card.id, input.plans);
  const events: CardPaymentEvent[] = [];

  const anchor = startOfDay(card.balanceAsOf ? toLocalDay(card.balanceAsOf) : new Date());
  const end = startOfDay(through);
  if (end < anchor) return events;

  // Flat, pre-sorted views of the rows. Avoids re-scanning a Map inside
  // the cycle loop (and keeps this ES5-target-compatible).
  const allRows: { plan: PayoffPlan; row: PlannedPayment }[] = [];
  for (const plan of plans) {
    for (const row of plan.payments ?? []) allRows.push({ plan, row });
  }
  const allExtras = allRows
    .filter(e => e.row.kind === 'EXTRA')
    .map(e => ({ ...e, date: startOfDay(toLocalDay(e.row.dueDate)) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  /**
   * Statement rows in date order, matched to a cycle by the window it covers
   * rather than by an exact day.
   *
   * They used to be looked up by day key against the day the cycle closed on,
   * which silently tied every plan to the card's due day as it was when the
   * plan was built. Change the due day — or move one payment — and no row
   * matched, so the card fell back to its minimum while the plan sat there
   * ACTIVE and apparently in force.
   */
  const statementRows = allRows
    .filter(e => e.row.kind !== 'EXTRA' && e.plan.mode !== 'LUMP_SUM')
    .map(e => ({ ...e, date: startOfDay(toLocalDay(e.row.dueDate)) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  /** Row ids already spent, so a cycle cannot claim one twice. */
  const spent = new Set<string>();
  const principalPct = card.minPaymentPrincipalPercent ?? 1;
  let balance = card.balance;

  // A card with no due day has no minimum cycle; only explicit rows apply.
  if (!card.dueDay) {
    const explicit = allRows
      .map(e => ({ ...e, date: startOfDay(toLocalDay(e.row.dueDate)) }))
      .filter(e => e.date >= anchor && e.date <= end)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    for (const e of explicit) {
      const opening = balance;
      const amount = Math.min(e.row.amount, Math.max(0, balance));
      balance = Math.max(0, balance - amount);
      events.push({
        cardId: card.id,
        cardName: card.name,
        planId: e.plan.id,
        paymentId: e.row.id,
        date: e.date,
        amount: round2(amount),
        paymentKind: e.row.kind,
        status: e.row.status,
        openingBalance: round2(opening),
        interestAccrued: 0,
        remainingAfter: round2(balance),
        isFinal: balance <= EPSILON,
      });
    }
    return events;
  }

  const dueDay = card.dueDay;
  let dueDate = firstPaymentDate(anchor, dueDay, anchor);
  let windowStart = anchor;
  let cycles = 0;

  while (cycles < MAX_CYCLES) {
    cycles += 1;
    if (balance <= EPSILON) break;
    if (windowStart >= end) break;

    // The cycle runs (windowStart, dueDate]. If `end` falls inside it we still
    // process the extras that landed before `end` — otherwise a lump sum dated
    // before the first due day would never be applied when the projection
    // window stops short of that due day.
    const cycleClosed = dueDate <= end;
    const windowEnd = cycleClosed ? dueDate : end;

    // --- 1. Extras inside (windowStart, windowEnd], in date order ----------
    // Applied BEFORE interest accrues: a payment made mid-cycle reduces the
    // balance the statement charges interest on, and — critically — reduces
    // the balance this month's minimum is computed from.
    const wStart = windowStart;
    const extras = allExtras.filter(e => e.date > wStart && e.date <= windowEnd);

    for (const extra of extras) {
      if (balance <= EPSILON) break;
      if (extra.row.status === 'SKIPPED') continue;
      const opening = balance;
      const stated = extra.row.actualAmount ?? extra.row.amount;
      const amount = Math.min(stated, Math.max(0, balance));
      if (amount <= 0) continue;
      balance = Math.max(0, balance - amount);
      events.push({
        cardId: card.id,
        cardName: card.name,
        planId: extra.plan.id,
        paymentId: extra.row.id,
        date: extra.date,
        amount: round2(amount),
        paymentKind: 'EXTRA',
        status: extra.row.status,
        openingBalance: round2(opening),
        interestAccrued: 0,
        remainingAfter: round2(balance),
        isFinal: balance <= EPSILON,
      });
    }

    // Partial trailing cycle: the statement has not closed, so no interest is
    // charged and no minimum is due.
    //
    // A PLAN ROW dated inside it still has to be drawn. Its date is the user's
    // choice, and once a payment is moved past a statement close it belongs to
    // the next cycle — which may close well beyond the horizon being projected
    // to. Skipping it here made a payment dragged two days later vanish from
    // the calendar entirely while sitting correctly in the database.
    if (!cycleClosed) {
      const pending = statementRows.filter(
        e => !spent.has(e.row.id) && e.date > wStart && e.date <= windowEnd
      );
      for (const e of pending) {
        if (balance <= EPSILON) break;
        spent.add(e.row.id);
        if (e.row.status === 'SKIPPED') continue;
        const opening = balance;
        const stated = e.row.actualAmount ?? e.row.amount;
        const amount = Math.min(stated, Math.max(0, balance));
        if (amount <= 0) continue;
        balance = Math.max(0, balance - amount);
        events.push({
          cardId: card.id,
          cardName: card.name,
          planId: e.plan.id,
          paymentId: e.row.id,
          date: e.date,
          amount: round2(amount),
          paymentKind: balance <= EPSILON ? 'FINAL' : e.row.kind === 'FINAL' ? 'FINAL' : 'SCHEDULED',
          status: e.row.status,
          openingBalance: round2(opening),
          interestAccrued: 0,
          remainingAfter: round2(balance),
          isFinal: balance <= EPSILON,
        });
      }
      break;
    }
    if (balance <= EPSILON) break;

    // --- 2. Interest on the post-extras balance ---------------------------
    const preInterest = balance;
    const interest = cycleInterest(preInterest, card.interestRate);
    balance += interest;

    // --- 3. The statement payment -----------------------------------------
    let stated: number;
    let kind: CardPaymentKind;
    let planId: string | null = null;
    let paymentId: string | null = null;
    let status: PaymentStatus = 'PLANNED';
    /** Where the payment is drawn. A computed minimum lands on the card's due
     *  day because that IS its date. A plan row lands on its OWN date — that
     *  date is the user's choice, and moving it is the whole point of being
     *  able to drag one. Drawing it on the due day instead made a move look
     *  like it had been rejected: the row changed in the database and the pill
     *  never left the due day. */
    let payDate = dueDate;

    // The earliest unspent row belonging to this cycle. Presence of a row is
    // what says the plan covers this cycle — once its rows run out, the card
    // goes back to its own minimum on its own.
    //
    // A row belongs to the cycle whose close it is NEAREST to, so the window
    // ends halfway to the next close. Both neighbouring rules are wrong:
    //
    //   Ending at this close dropped a payment moved a few days late out of
    //   its own cycle, so the card charged a minimum as well and the month
    //   paid twice.
    //
    //   Ending at the NEXT close let a cycle swallow the following cycle's
    //   row. Every plan generated from today has that shape — its first row is
    //   the next due date, while the card replays a cycle or two from its own
    //   balanceAsOf first — so rows shifted a cycle early right down the
    //   schedule and the last one went unclaimed, leaving the card to finish
    //   on minimums.
    //
    // Halfway keeps a row on its own due day where it belongs, keeps a row
    // moved a few days off it in the same cycle, and leaves a plan that starts
    // later to be preceded by minimums.
    const nextClose = payDayFor(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDay);
    const to = startOfDay(new Date((dueDate.getTime() + nextClose.getTime()) / 2));
    const from = windowStart;
    const entry = statementRows.find(
      e => !spent.has(e.row.id) && e.date > from && e.date <= to
    );

    if (entry) {
      const { plan, row } = entry;
      spent.add(row.id);
      planId = plan.id;
      paymentId = row.id;
      status = row.status;
      kind = row.kind === 'FINAL' ? 'FINAL' : 'SCHEDULED';
      stated = row.actualAmount ?? row.amount;
      if (row.status === 'SKIPPED') stated = 0;
      payDate = entry.date;
    } else {
      // Not covered — the card is on its own minimum, computed from the
      // post-extras PRE-interest balance so a lump sum immediately shrinks it.
      kind = 'MINIMUM';
      stated = computeMinimumPayment(
        preInterest,
        card.interestRate,
        card.minimumPayment,
        principalPct
      );
    }

    const amount = Math.min(stated, Math.max(0, balance));
    balance = Math.max(0, balance - amount);

    if (amount > 0) {
      events.push({
        cardId: card.id,
        cardName: card.name,
        planId,
        paymentId,
        date: payDate,
        amount: round2(amount),
        paymentKind: balance <= EPSILON ? 'FINAL' : kind,
        status,
        openingBalance: round2(preInterest),
        interestAccrued: round2(interest),
        remainingAfter: round2(balance),
        isFinal: balance <= EPSILON,
      });
    }

    windowStart = dueDate;
    dueDate = payDayFor(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDay);
  }

  return events;
}

/**
 * The card's balance on a given date.
 *
 * Replaces `calculateCurrentCreditCardBalance`. Two behavioral fixes come free:
 * interest now accrues every month (the old version accrued only in months
 * that happened to contain a payment), and it no longer mutates the caller's
 * Date argument.
 */
export function projectCardBalance(
  card: CreditCard,
  plans: PayoffPlan[],
  asOfDate?: Date
): number {
  const target = startOfDay(asOfDate ?? new Date());
  const anchor = startOfDay(card.balanceAsOf ? toLocalDay(card.balanceAsOf) : new Date());
  if (target <= anchor) return card.balance;

  const events = projectCardPayments({ card, plans, through: target });
  if (events.length === 0) return card.balance;
  return events[events.length - 1].remainingAfter;
}

/** The card's minimum payment right now, for display. */
export function currentMinimumPayment(card: CreditCard, plans: PayoffPlan[]): number {
  const balance = projectCardBalance(card, plans);
  return computeMinimumPayment(
    balance,
    card.interestRate,
    card.minimumPayment,
    card.minPaymentPrincipalPercent ?? 1
  );
}

/** The active covering plan for a card, if any. LUMP_SUM plans don't count —
 *  they don't replace the minimum schedule. */
export function activeCoveringPlan(cardId: string, plans: PayoffPlan[]): PayoffPlan | null {
  return activePlansFor(cardId, plans).find(p => p.mode !== 'LUMP_SUM') ?? null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
